/**
 * Automatic sign-in behind Google Identity-Aware Proxy (IAP).
 *
 * When IAP fronts the web host it has already authenticated the person with
 * Google and passes their identity to nginx, which echoes it at
 * /__/iap/whoami. The login page then starts Google sign-in with that address
 * as a login hint, so Google signs the same account in without a chooser.
 *
 * The header is only a hint: Identity Platform, the beforeSignIn function and
 * the API still decide who gets in. Safeguards against loops and against
 * trapping someone in the wrong account:
 *  - tried at most once per browser session;
 *  - never after the person deliberately signed out (they may want another
 *    account) until they sign in again;
 *  - never when the page already shows a sign-in problem (e.g. not a user).
 */
import { isWorkspaceDomain } from '@sss/auth-rbac';

const ATTEMPTED = 'sss.iapAutoSignIn';
const SIGNED_OUT = 'sss.signedOut';

function get(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function set(store: Storage | undefined, key: string, value: string | null): void {
  try {
    if (value === null) store?.removeItem(key);
    else store?.setItem(key, value);
  } catch {
    /* storage unavailable — auto sign-in simply won't repeat-guard */
  }
}

/** IAP sends "accounts.google.com:user@g4s.com"; returns the email or null. */
export function parseIapEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const email = raw.replace(/^accounts\.google\.com:/, '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

/** The IAP identity, if the site is behind IAP and the person passed it. */
export async function iapIdentity(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const r = await fetchImpl('/__/iap/whoami', { cache: 'no-store', credentials: 'same-origin' });
    if (!r.ok) return null;
    return parseIapEmail(((await r.json()) as { email?: unknown }).email);
  } catch {
    return null;
  }
}

export function shouldTryIapSignIn(opts: { hasProblemMessage: boolean }): boolean {
  if (opts.hasProblemMessage) return false;
  if (get(globalThis.sessionStorage, ATTEMPTED)) return false;
  if (get(globalThis.localStorage, SIGNED_OUT)) return false;
  return true;
}

export function markIapAttempt(): void {
  set(globalThis.sessionStorage, ATTEMPTED, '1');
}

/** A deliberate sign-out: don't auto sign in again until the next manual sign-in. */
export function markSignedOut(): void {
  set(globalThis.localStorage, SIGNED_OUT, '1');
}

export function clearSignedOut(): void {
  set(globalThis.localStorage, SIGNED_OUT, null);
}

/** Only hint Workspace addresses we would accept anyway. */
export function usableIapEmail(email: string | null, workspaceDomains: string[]): string | null {
  if (!email) return null;
  return isWorkspaceDomain(email.split('@')[1], workspaceDomains) ? email : null;
}
